/**
 * Stage 1 — column histogram accumulation.
 *
 * ONE traversal over the visible octree nodes splats every point inside the
 * detection box into a 2D XY column grid of z-histograms — O(total points),
 * replacing the old per-ray octree re-traversal (each ray re-scanned every
 * node). Node coverage matches the previous ray path: `visibleNodes`, i.e.
 * whatever LOD the render budget loaded. Points are read from
 * `geometryNode.geometry.attributes.position` (node-local floats) and
 * transformed by `sceneNode.matrixWorld` — the same access pattern as
 * `getPointsAlongRay`, which is the only one PPC nodes support.
 */

import * as THREE from 'three';
import type { PointCloudOctree } from '../../PointCloudOctree';
import type { ColumnGrid } from './types';
import type { FloorDetectionTuning } from './tuning';

/** Allocate the (lazily filled) column grid for a detection box. */
export function createColumnGrid(boundingBox: THREE.Box3, tuning: FloorDetectionTuning): ColumnGrid {
  const size = new THREE.Vector3();
  boundingBox.getSize(size);

  // Grow the cell when the selection would exceed the column budget.
  let cellSize = tuning.CELL_SIZE_XY;
  const rawColumns = Math.ceil(size.x / cellSize) * Math.ceil(size.y / cellSize);
  if (rawColumns > tuning.MAX_COLUMNS) {
    cellSize = Math.sqrt((size.x * size.y) / tuning.MAX_COLUMNS);
  }
  const nx = Math.max(1, Math.ceil(size.x / cellSize));
  const ny = Math.max(1, Math.ceil(size.y / cellSize));

  // Grow the bin when the z-range would exceed the bin budget.
  let binSize = tuning.BIN_SIZE_Z;
  if (Math.ceil(size.z / binSize) + 1 > tuning.MAX_BINS) {
    binSize = size.z / (tuning.MAX_BINS - 1);
  }
  const binCount = Math.max(1, Math.ceil(size.z / binSize) + 1);

  return {
    nx,
    ny,
    cellSize,
    binSize,
    binCount,
    originX: boundingBox.min.x,
    originY: boundingBox.min.y,
    originZ: boundingBox.min.z,
    columns: new Array<Uint16Array | null>(nx * ny).fill(null),
    totalPoints: 0,
  };
}

/** Half-plane form of a convex quad edge: inside ⇔ ex*x + ey*y >= d for all edges. */
interface QuadEdge {
  ex: number;
  ey: number;
  d: number;
}

/**
 * Precompute inward half-plane tests for a convex quad (the possibly rotated
 * selection rectangle). Returns null when no corners were given.
 */
export function buildQuadEdges(corners: THREE.Vector2[] | undefined): QuadEdge[] | null {
  if (!corners || corners.length < 4) return null;
  // Winding: flip the edge normals if the polygon is clockwise.
  let area2 = 0;
  for (let i = 0; i < 4; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    area2 += a.x * b.y - b.x * a.y;
  }
  const sign = area2 >= 0 ? 1 : -1;
  const edges: QuadEdge[] = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    const ex = -(b.y - a.y) * sign;
    const ey = (b.x - a.x) * sign;
    edges.push({ ex, ey, d: ex * a.x + ey * a.y });
  }
  return edges;
}

/**
 * Splat one node's points into the grid. Reads the position attribute's
 * node-local floats and inlines the affine world transform — no per-point
 * allocations.
 *
 * @param positions Node-local xyz triplets (geometry position attribute array).
 * @param count     Number of points (attribute count).
 */
export function accumulateNode(grid: ColumnGrid, positions: ArrayLike<number>, count: number, matrixWorld: THREE.Matrix4, box: THREE.Box3, quadEdges: QuadEdge[] | null): void {
  const m = matrixWorld.elements;

  const { nx, ny, cellSize, binSize, binCount, originX, originY, originZ } = grid;
  const minX = box.min.x;
  const minY = box.min.y;
  const minZ = box.min.z;
  const maxX = box.max.x;
  const maxY = box.max.y;
  const maxZ = box.max.z;

  for (let i = 0; i < count; i++) {
    const idx = i * 3;
    const x = positions[idx];
    const y = positions[idx + 1];
    const z = positions[idx + 2];

    const wx = m[0] * x + m[4] * y + m[8] * z + m[12];
    const wy = m[1] * x + m[5] * y + m[9] * z + m[13];
    const wz = m[2] * x + m[6] * y + m[10] * z + m[14];

    if (wx < minX || wx > maxX || wy < minY || wy > maxY || wz < minZ || wz > maxZ) continue;

    if (quadEdges) {
      let inside = true;
      for (const e of quadEdges) {
        if (e.ex * wx + e.ey * wy < e.d) {
          inside = false;
          break;
        }
      }
      if (!inside) continue;
    }

    const ix = Math.min(nx - 1, Math.floor((wx - originX) / cellSize));
    const iy = Math.min(ny - 1, Math.floor((wy - originY) / cellSize));
    const iz = Math.min(binCount - 1, Math.floor((wz - originZ) / binSize));

    const columnIndex = iy * nx + ix;
    let column = grid.columns[columnIndex];
    if (!column) {
      column = new Uint16Array(binCount);
      grid.columns[columnIndex] = column;
    }
    if (column[iz] < 0xffff) column[iz]++;
    grid.totalPoints++;
  }
}

/** One node ready for accumulation: resolved position array + world transform. */
export interface AccumulationNode {
  positions: ArrayLike<number>;
  count: number;
  matrixWorld: THREE.Matrix4;
}

/**
 * Collect the visible nodes of all point clouds whose world AABB intersects
 * the detection box, with their position attributes resolved. Nodes without a
 * scene node or loaded geometry are skipped (same guards as
 * getPointsAlongRay).
 */
export function collectNodesInBox(pointclouds: PointCloudOctree[], box: THREE.Box3): AccumulationNode[] {
  const nodes: AccumulationNode[] = [];
  const worldBox = new THREE.Box3();
  for (const pc of pointclouds) {
    if (!pc.visible) continue;
    for (const node of pc.visibleNodes) {
      if (!node.sceneNode || !node.geometryNode?.geometry) continue;
      const positionAttribute = node.geometryNode.geometry.attributes.position as THREE.BufferAttribute | undefined;
      if (!positionAttribute?.array) continue;
      worldBox.copy(node.geometryNode.boundingBox).applyMatrix4(pc.matrixWorld);
      if (!worldBox.intersectsBox(box)) continue;
      nodes.push({
        positions: positionAttribute.array as ArrayLike<number>,
        count: positionAttribute.count,
        matrixWorld: node.sceneNode.matrixWorld,
      });
    }
  }
  return nodes;
}
